if (typeof DosageCalculatorMonths !== "function") {
  class DosageCalculatorMonths extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        full: "is-full",
        partialHigh: "is-partial-high", // >= half the month covered
        partialLow: "is-partial-low", // < half the month covered
      };

      this.selectors = {
        dosagesScript: "[data-dosages]",
        weightInput: "[data-weight-input]",
        unitInputs: "[data-unit-input]",
        selectedUnit: "[data-unit-input]:checked",
        resultMonths: "[data-result-months]",
        resultMonthsLabel: "[data-result-months-label]",
        resultDose: "[data-result-dose]",
        calendar: "[data-months]",
      };

      this.elements = {
        weightInput: this.querySelector(this.selectors.weightInput),
        unitInputs: this.querySelectorAll(this.selectors.unitInputs),
        resultMonths: this.querySelector(this.selectors.resultMonths),
        resultMonthsLabel: this.querySelector(this.selectors.resultMonthsLabel),
        resultDose: this.querySelector(this.selectors.resultDose),
        calendar: this.querySelector(this.selectors.calendar),
      };

      this.settings = {
        daysPerMonth: 30,
        monthsShown: 6,
        lbsPerKg: 2.205, // same factor as component-dosages-calculator.js
        storageKey: "selectedDogWeight", // shared with product-custom-siblings.js (stored in lbs)
      };

      // "80 days" -> 80  (same parsing as product-custom-siblings.js)
      this.baseDays =
        parseInt((this.dataset.baseDays || "").match(/\d+/)?.[0], 10) || 0;
      this.doseUnit = (this.dataset.doseUnit || "").trim().toLowerCase();
      this.locale = document.documentElement.lang || "en";
      // "months" -> "2.7 months" | "months_days" -> "2 months 20 days"
      this.durationFormat = this.dataset.durationFormat || "months";
      this.labels = {
        month: this.dataset.labelMonth || "",
        months: this.elements.resultMonthsLabel?.textContent.trim() || "",
        day: this.dataset.labelDay || "",
        days: this.dataset.labelDays || "",
      };

      this.dosages = this.arrangeData();
      this.unit = "lbs";
      this.weight = 0;

      this.buildCalendar();
      this.restoreWeight();
      this.addListeners();
      this.render();
    }

    /**
     * Same shape as component-dosages-calculator.js: one lbs list and one kg
     * list (kg bounds rounded), each entry holding the days one product lasts.
     */
    arrangeData() {
      let rows = [];
      try {
        rows = JSON.parse(
          this.querySelector(this.selectors.dosagesScript)?.textContent || "[]",
        );
      } catch (e) {
        rows = [];
      }

      const lbs = [];
      const kg = [];

      rows.forEach((row) => {
        const min = parseInt(row.start, 10);
        const max = parseInt(row.end, 10);
        const dose = parseFloat(row.dose);
        if (!dose) return;

        const entry = { dose, days: this.baseDays / dose };
        lbs.push({ ...entry, min, max });
        kg.push({
          ...entry,
          min: Math.round(min / this.settings.lbsPerKg),
          max: Math.round(max / this.settings.lbsPerKg),
        });
      });

      return {
        lbs: lbs.sort((a, b) => a.min - b.min),
        kg: kg.sort((a, b) => a.min - b.min),
      };
    }

    addListeners() {
      this.elements.weightInput?.addEventListener("input", () => {
        this.setWeight();
        this.saveWeight();
        this.render();
      });

      this.elements.unitInputs.forEach((radio) => {
        radio.addEventListener("change", () => {
          const previousUnit = this.unit;
          this.setUnit();
          this.convertInput(previousUnit, this.unit);
          this.render();
        });
      });
    }

    setUnit() {
      this.unit = this.querySelector(this.selectors.selectedUnit)?.value || "lbs";
    }

    setWeight() {
      this.weight = parseFloat(this.elements.weightInput?.value) || 0;
    }

    /** Keep the number meaningful when the customer switches LBS <-> KG. */
    convertInput(from, to) {
      if (from === to || !this.weight) return;
      const converted =
        to === "kg"
          ? this.weight / this.settings.lbsPerKg
          : this.weight * this.settings.lbsPerKg;
      this.weight = Math.round(converted);
      if (this.elements.weightInput) this.elements.weightInput.value = this.weight;
    }

    weightInLbs() {
      return this.unit === "kg"
        ? this.weight * this.settings.lbsPerKg
        : this.weight;
    }

    /**
     * Pre-fill with the weight the customer already entered elsewhere on the
     * page; otherwise start at the first weight of the second range
     * (e.g. "23" for ranges 0-22 / 23-55) so the block never renders empty.
     */
    restoreWeight() {
      let stored = 0;
      try {
        stored = parseFloat(localStorage.getItem(this.settings.storageKey)) || 0;
      } catch (e) {
        stored = 0;
      }

      const fallback = this.dosages.lbs[1]?.min || this.dosages.lbs[0]?.min || 0;
      this.weight = Math.round(stored || fallback);
      if (this.elements.weightInput) this.elements.weightInput.value = this.weight;
    }

    saveWeight() {
      try {
        localStorage.setItem(
          this.settings.storageKey,
          Math.round(this.weightInLbs()).toString(),
        );
      } catch (e) {
        /* storage unavailable - ignore */
      }
    }

    /** Range containing the weight (last match, like the original calculator). */
    findDosage() {
      const list = this.dosages[this.unit] || [];
      if (!this.weight || !list.length) return null;

      const matches = list.filter(
        (d) => this.weight >= d.min && this.weight <= d.max,
      );
      if (matches.length) return matches[matches.length - 1];

      // heavier than the last range ("110 lb +") -> use the last range
      const last = list[list.length - 1];
      return this.weight > last.max ? last : null;
    }

    render() {
      const dosage = this.findDosage();

      if (!dosage || !this.baseDays) {
        this.setText(this.elements.resultMonths, "–");
        this.setText(this.elements.resultDose, "–");
        this.paintCalendar(0);
        return;
      }

      const days = Math.round(dosage.days);
      const months = days / this.settings.daysPerMonth;

      this.renderDuration(days, months);
      this.setText(
        this.elements.resultDose,
        `${this.formatFraction(dosage.dose)} ${this.formatUnit(dosage.dose)}`.trim(),
      );
      this.paintCalendar(months);
    }

    /**
     * "months":      2.7 | months
     * "months_days": 2 months 20 days   (or "20 days" / "2 months" when a part is 0)
     */
    renderDuration(days, months) {
      if (this.durationFormat === "months_days") {
        const wholeMonths = Math.floor(months);
        const restDays = days - wholeMonths * this.settings.daysPerMonth;
        const parts = [];

        if (wholeMonths > 0) {
          parts.push(`${wholeMonths} ${this.pluralize(wholeMonths, this.labels.month, this.labels.months)}`);
        }
        if (restDays > 0 || wholeMonths === 0) {
          parts.push(`${restDays} ${this.pluralize(restDays, this.labels.day, this.labels.days)}`);
        }

        this.setText(this.elements.resultMonths, parts.join(" ").trim());
        this.setText(this.elements.resultMonthsLabel, "");
        return;
      }

      const monthsRounded = Math.round(months * 10) / 10;
      this.setText(this.elements.resultMonths, this.formatMonths(monthsRounded));
      this.setText(
        this.elements.resultMonthsLabel,
        this.pluralize(monthsRounded, this.labels.month, this.labels.months),
      );
    }

    pluralize(count, singular, plural) {
      if (count === 1 && singular) return singular;
      return plural || singular;
    }

    /** One chip per month, starting with the current month. */
    buildCalendar() {
      if (!this.elements.calendar) return;

      const formatter = new Intl.DateTimeFormat(this.locale, { month: "short" });
      const now = new Date();
      const chips = [];

      for (let i = 0; i < this.settings.monthsShown; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const label = formatter.format(date).replace(/\.$/, "");
        chips.push(`<span class="calendar__month">${label}</span>`);
      }

      this.elements.calendar.innerHTML = chips.join("");
      this.elements.months = this.elements.calendar.querySelectorAll(".calendar__month");
    }

    /**
     * Full months -> dark green; the remaining fraction -> light green when
     * at least half the month is covered, lighter green when less than half.
     */
    paintCalendar(months) {
      if (!this.elements.months) return;

      const full = Math.floor(months);
      const fraction = months - full;
      const hasPartial = fraction >= 0.05;
      const isHigh = fraction >= 0.5;

      this.elements.months.forEach((chip, index) => {
        const isPartial = hasPartial && index === full;
        chip.classList.toggle(this.classes.full, index < full);
        chip.classList.toggle(this.classes.partialHigh, isPartial && isHigh);
        chip.classList.toggle(this.classes.partialLow, isPartial && !isHigh);
      });
    }

    setText(element, value) {
      if (element) element.textContent = value;
    }

    formatMonths(months) {
      try {
        return new Intl.NumberFormat(this.locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        }).format(months);
      } catch (e) {
        return `${months}`;
      }
    }

    /**
     * 0.5 -> "1/2", 1.5 -> "1 1/2", 0.33 -> "1/3", 2 -> "2", 0.4 -> "0.4"
     * (rendered with unicode vulgar fractions)
     */
    formatFraction(number) {
      const fractions = [
        [0.125, "⅛"],
        [0.25, "¼"],
        [0.333, "⅓"],
        [0.375, "⅜"],
        [0.5, "½"],
        [0.625, "⅝"],
        [0.666, "⅔"],
        [0.75, "¾"],
        [0.875, "⅞"],
      ];

      const whole = Math.floor(number);
      const remainder = number - whole;

      if (remainder < 0.01) return `${whole}`;

      const match = fractions.find(([value]) => Math.abs(value - remainder) < 0.02);
      if (match) return `${whole > 0 ? whole : ""}${match[1]}`;

      return `${parseFloat(number.toFixed(2))}`;
    }

    formatUnit(dose) {
      const abbreviations = {
        teaspoon: "tsp",
        teaspoons: "tsp",
        tablespoon: "tbsp",
        tablespoons: "tbsp",
      };

      if (abbreviations[this.doseUnit]) return abbreviations[this.doseUnit];
      if (!this.doseUnit) return "";

      // simple pluralisation for units like "capsule" / "scoop"
      if (dose > 1 && !this.doseUnit.endsWith("s")) return `${this.doseUnit}s`;
      return this.doseUnit;
    }
  }

  if (!customElements.get("dosage-calculator-months")) {
    customElements.define("dosage-calculator-months", DosageCalculatorMonths);
  }
}
